import { range, lt } from '../math/util'
import { Point } from '../math/point'
import { vector } from '../math/vector'
import { Ship } from './ship'
import clone from 'immutability-helper'

export enum WeaponType {
  Phaser1,
  Phaser2,
  Phaser3,
  Disruptor,
  Photon
}

class WeaponClass {
  constructor(readonly name: string, readonly effect: WeaponType, readonly range: number) {}

  create(id: string, arc: number[], mountPoint: number, attack: number): Weapon {
    switch (this.effect) {
      case WeaponType.Phaser1: return new PhaserWeapon(id, this.name, this.effect, this.range, arc, mountPoint, attack, 2, 45)
      case WeaponType.Phaser2: return new PhaserWeapon(id, this.name, this.effect, this.range, arc, mountPoint, attack, 1, 35)
      case WeaponType.Phaser3: return new PhaserWeapon(id, this.name, this.effect, this.range, arc, mountPoint, attack, 1, 15)
      case WeaponType.Disruptor: return new DisruptorWeapon(id, this.name, this.effect, this.range, arc, mountPoint, attack, 1)
      case WeaponType.Photon: return new PhotonWeapon(id, this.name, this.effect, this.range, arc, mountPoint, attack)
    }
  }
}

export const PHASER_1  = new WeaponClass("Phaser-1", WeaponType.Phaser1, 80)
export const PHASER_2  = new WeaponClass("Phaser-2", WeaponType.Phaser2, 60)
export const PHASER_3  = new WeaponClass("Phaser-3", WeaponType.Phaser3, 40)
export const DISRUPTOR = new WeaponClass("Disruptor", WeaponType.Disruptor, 110)
export const PHOTON    = new WeaponClass("Photon", WeaponType.Photon, 130)//70)

export interface Weapon {
  readonly id: string
  readonly name: string
  readonly weaponType: WeaponType
  readonly range: number
  readonly arc: number[]
  readonly mountPoint: number
  readonly attack: number

  readonly charge: number
  readonly chargeMax: number

  readonly hits: number
  readonly auto: number

  damage(targetRange: number): [number, number, number]
  canAttack(source: Ship, target: Ship): boolean
  fire(hits: number, auto: number): Weapon
  endPhase(): Weapon
}

class WeaponBase implements Weapon {
  readonly charge: number = 0

  readonly hits = 0
  readonly auto = 0

  constructor(
    readonly id: string,
    readonly name: string,
    readonly weaponType: WeaponType,
    readonly range: number,
    readonly arc: number[],
    readonly mountPoint: number,
    readonly attack: number,
    readonly chargeMax: number) {}

  protected rangeHitModifier(targetRange: number): number {
    if (targetRange > this.range/2) {
      return -1
    }

    return 0
  }

  protected toHitModifier(targetRange: number): number {
    return 0
  }

  protected damageMultiplier(targetRange: number): number {
    return 1
  }

  protected damageModifier(targetRange: number): number {
    return 0
  }

  damage(targetRange: number): [number, number, number] {
    if (targetRange > this.range) {
      return [0, 0, 0]
    }
    const toHitModifier = this.toHitModifier(targetRange) + this.rangeHitModifier(targetRange)
    const [misses, hits, auto] = attack(this.attack, toHitModifier)
    const multiplier = this.damageMultiplier(targetRange)
    const modifier = this.damageModifier(targetRange)

    return [misses, hits*(multiplier+modifier), auto*(multiplier+modifier)]
  }

  canAttack(source: Ship, target: Ship): boolean {
    if (this.charge === 0) {
      const quadrant = source.quadrant(target.position)
      if (this.arc.some(arc => arc === quadrant)) {
        const v = vector(source.position, target.position)
        if (lt(v.length(), this.range)) {
          return true
        }
      }
    }
      return false
  }

  fire(hits: number, auto: number): Weapon {
    return clone(this as Weapon, { charge: {$set: this.chargeMax}, hits: {$set: hits}, auto: {$set: auto} })
  }

  endPhase(): Weapon {
    if (this.charge === 1) {
      return clone(this as Weapon, {charge: {$set: 0}, hits: {$set: 0}, auto: {$set: 0} })
    }
    if (this.charge > 0) {
      return clone(this as Weapon, { charge: {$set: this.charge-1} })
    }

    return this
  }
}

class PhaserWeapon extends WeaponBase {
  constructor(
    id: string,
    name: string,
    weaponType: WeaponType,
    range: number,
    arc: number[],
    mountPoint: number,
    attack: number,
    readonly accurate: number,
    readonly killZone: number
    ) {
      super(id, name, weaponType, range, arc, mountPoint, attack, 3)
    }

  protected toHitModifier(targetRange: number): number {
    return this.accurate
  }

  protected damageMultiplier(targetRange: number): number {
    if (targetRange <= this.killZone) {
      return 2
    }
    return 1
  }
}

class PhotonWeapon extends WeaponBase {
  constructor(
    id: string,
    name: string,
    weaponType: WeaponType,
    range: number,
    arc: number[],
    mountPoint: number,
    attack: number
    ) {
      super(id, name, weaponType, range, arc, mountPoint, attack, 4)
    }

  protected damageMultiplier(targetRange: number): number {
    return 4
  }
}

class DisruptorWeapon extends WeaponBase {
  constructor(
    id: string,
    name: string,
    weaponType: WeaponType,
    range: number,
    arc: number[],
    mountPoint: number,
    attack: number,
    readonly accurate: number
    ) {
      super(id, name, weaponType, range, arc, mountPoint, attack, 4)
    }

  protected toHitModifier(targetRange: number): number {
    return this.accurate
  }
  protected damageMultiplier(targetRange: number): number {
    return 2
  }
}

export function roll(count: number): number[] {
  return range(count).map((_) => Math.floor(Math.random() * 5) + 1)
}

function attack(count: number, toHitModifier: number = 0): [number, number, number] {
  const toHit = 4 - toHitModifier

  const ds = roll(count)

  const misses = ds.filter(d => d == 1).length

  const auto = ds.filter(d => d == 6).length

  const hits = ds.filter(d => (d > 1) && (d < 6) && (d >= toHit)).length

  return [misses, hits, auto]
}
